import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { formatJobSeverity } from '@/features/jobs/lib/format-job-severity'
import type { JobSeverity, JobType } from '@/features/jobs/types/job'
import {
  apiGetServiceLevelAgreementByPartnerReport,
  apiGetServiceLevelAgreementReport,
} from '@/features/reports/api/api-get-jobs-report'
import { DrilldownLink } from '@/features/reports/components/drilldown-link'
import { JobsReportFiltersPanel } from '@/features/reports/components/jobs-report-filters-panel'
import { JobsReportShell } from '@/features/reports/components/jobs-report-shell'
import { ReportCategoryChart } from '@/features/reports/components/report-category-chart'
import { useJobsReportPage } from '@/features/reports/hooks/use-jobs-report-page'
import { buildJobsReportBaseColumns } from '@/features/reports/lib/build-jobs-report-base-columns'
import { formatJobsReportFilterTitle } from '@/features/reports/lib/format-jobs-report-filter-title'
import {
  formatSlaCategory,
  SLA_CATEGORIES,
  slaCategoryColour,
  type SlaCategory,
} from '@/features/reports/lib/format-sla-category'
import { nextJobsReportSort } from '@/features/reports/lib/jobs-report-sort'
import {
  buildServiceLevelAgreementReportSearchParams,
  defaultServiceLevelAgreementReportSearchParams,
  parseServiceLevelAgreementReportSearchParams,
  serviceLevelAgreementByPartnerPath,
  serviceLevelAgreementPath,
} from '@/features/reports/lib/jobs-report-search-params'
import { slaCategoryFor, slaExceeded } from '@/features/reports/lib/sla-elapsed-days'
import {
  emptyServiceLevelAgreementReportFilters,
  type JobsByPartnerReportSummary,
  type ServiceLevelAgreementReportFilters,
  type ServiceLevelAgreementReportSummary,
  type SlaJob,
} from '@/features/reports/types/jobs-report'
import {
  ListTable,
  LoadingSpinner,
  PaginationLinks,
  SelectField,
  type ListTableColumn,
  type SelectOption,
} from '@/shared/components'

type SlaReportSummary =
  | ServiceLevelAgreementReportSummary
  | JobsByPartnerReportSummary

const emptySummary = (): SlaReportSummary => ({
  by_sla_category: [],
})

const slaCategoryOptions: SelectOption[] = SLA_CATEGORIES.map(({ value, label }) => ({
  value,
  label,
}))

const severityOptions: SelectOption[] = (
  ['expedited', 'urgent', 'bau'] as JobSeverity[]
).map((severity) => ({
  value: severity,
  label: formatJobSeverity(severity),
}))

const isPartnerSummary = (
  summary: SlaReportSummary
): summary is JobsByPartnerReportSummary => 'by_partner' in summary

const extraPartnerRows = (reportSummary: SlaReportSummary) =>
  isPartnerSummary(reportSummary) ? reportSummary.by_partner : []

const slaFilterTitle = (filters: ServiceLevelAgreementReportFilters) => {
  const parts: string[] = []

  if (filters.severity) {
    parts.push(`Severity: ${formatJobSeverity(filters.severity)}`)
  }

  if (filters.sla_category) {
    parts.push(`SLA status: ${formatSlaCategory(filters.sla_category)}`)
  }

  return parts.length > 0 ? parts.join(' · ') : undefined
}

export const ServiceLevelAgreementReportPage = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isPartnerView = pathname === '/reports/service-level-agreement-by-partner'

  const {
    filters,
    page,
    chartType,
    sort,
    jobs,
    summary,
    pagination,
    isInitialLoading,
    error,
    isFiltersOpen,
    setIsFiltersOpen,
    updateSearch,
    chartTitle,
  } = useJobsReportPage<
    ServiceLevelAgreementReportFilters,
    SlaJob,
    SlaReportSummary
  >({
    parseSearchParams: parseServiceLevelAgreementReportSearchParams,
    buildSearchParams: buildServiceLevelAgreementReportSearchParams,
    defaultSearchParams: defaultServiceLevelAgreementReportSearchParams,
    fetchReport: isPartnerView
      ? apiGetServiceLevelAgreementByPartnerReport
      : apiGetServiceLevelAgreementReport,
    emptySummary,
    formatFilterTitle: (nextFilters, labels) =>
      formatJobsReportFilterTitle(
        nextFilters,
        labels,
        slaFilterTitle(nextFilters)
      ),
    loadErrorMessage: isPartnerView
      ? 'Failed to load service level agreement by partner report'
      : 'Failed to load service level agreement report',
    extraPartnerRows,
  })

  const slaCategoryTo = (slaCategory: SlaCategory) =>
    serviceLevelAgreementByPartnerPath(
      { ...filters, sla_category: slaCategory },
      1,
      chartType,
      sort
    )

  const partnerTo = (assignedPartnerId: number) =>
    serviceLevelAgreementPath(
      { ...filters, assigned_partner_id: assignedPartnerId },
      1,
      chartType,
      sort
    )

  const currentPath = isPartnerView
    ? serviceLevelAgreementByPartnerPath
    : serviceLevelAgreementPath

  const jobTypeTo = (jobType: JobType) =>
    currentPath({ ...filters, job_type: jobType }, 1, chartType, sort)

  const severityTo = (severity: JobSeverity) =>
    currentPath({ ...filters, severity }, 1, chartType, sort)

  const columns: ListTableColumn<SlaJob>[] = buildJobsReportBaseColumns({
    filters,
    partnerTo,
    jobTypeTo,
    primaryColumns: [
      {
        id: 'severity',
        header: 'Severity',
        cell: (job) => (
          <DrilldownLink
            label={formatJobSeverity(job.severity)}
            active={filters.severity === job.severity}
            to={severityTo(job.severity)}
          />
        ),
      },
      {
        id: 'sla_category',
        header: 'SLA',
        cell: (job) => {
          const slaCategory = slaCategoryFor(job)
          if (!slaCategory) {
            return ''
          }

          return (
            <DrilldownLink
              label={formatSlaCategory(slaCategory)}
              active={filters.sla_category === slaCategory}
              to={slaCategoryTo(slaCategory)}
            />
          )
        },
      },
    ],
  })

  const chartMatchesView = isPartnerView
    ? isPartnerSummary(summary)
    : !isPartnerSummary(summary)
  const isChartLoading = isInitialLoading || !chartMatchesView

  const tableContent = (() => {
    if (isInitialLoading) {
      return (
        <LoadingSpinner
          position="center"
          label="Loading service level agreement jobs"
        />
      )
    }

    if (error) {
      return (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )
    }

    return (
      <>
        <ListTable
          columns={columns}
          data={jobs}
          getRowKey={(job) => job.id}
          emptyMessage="No jobs found."
          onRowClick={(job) => navigate(`/jobs/${job.id}`)}
          getRowClassName={(job) =>
            slaExceeded(job)
              ? 'bg-destructive/20 hover:bg-destructive/30'
              : undefined
          }
          sort={{ columnId: sort.column, direction: sort.direction }}
          onSort={(columnId) =>
            updateSearch(filters, 1, chartType, nextJobsReportSort(sort, columnId))
          }
        />

        <PaginationLinks
          pagination={pagination}
          onPageChange={(nextPage) => updateSearch(filters, nextPage)}
          className="pt-4"
        />
      </>
    )
  })()

  const chartRows = isPartnerSummary(summary)
    ? summary.by_partner.map((row) => ({
        key: String(row.assigned_partner_id),
        label: row.assigned_partner_name,
        count: row.count,
      }))
    : summary.by_sla_category.map((row) => ({
        key: row.sla_category,
        label: formatSlaCategory(row.sla_category),
        count: row.count,
        colour: slaCategoryColour(row.sla_category),
      }))

  return (
    <>
      <JobsReportShell
        chartAriaLabel={
          isPartnerView ? 'SiteWorks partner breakdown' : 'SLA breakdown'
        }
        chartTitle={chartTitle}
        isLoading={isChartLoading}
        error={error}
        onOpenFilters={() => setIsFiltersOpen(true)}
        chart={
          <ReportCategoryChart
            key={chartRows.map((row) => `${row.key}:${row.count}`).join('|')}
            rows={chartRows}
            chartType={chartType}
            onChartTypeChange={(nextChartType) =>
              updateSearch(filters, page, nextChartType)
            }
            onCategoryClick={(key) =>
              navigate(
                isPartnerView
                  ? partnerTo(Number(key))
                  : slaCategoryTo(key as SlaCategory)
              )
            }
            emptyMessage="No jobs match the current filters."
          />
        }
        table={tableContent}
        filtersPanel={
          <JobsReportFiltersPanel
            open={isFiltersOpen}
            onOpenChange={setIsFiltersOpen}
            filters={filters}
            onApply={(nextFilters) => updateSearch(nextFilters, 1)}
            title="Filter service level agreement"
            idPrefix="sla-filter"
            emptyFilters={emptyServiceLevelAgreementReportFilters}
            renderPrimaryDimension={(draft, setDraft) => (
              <>
                <SelectField
                  id="sla-filter-severity"
                  label="Severity"
                  value={draft.severity}
                  onValueChange={(severity) =>
                    setDraft((current) => ({
                      ...current,
                      severity: severity as JobSeverity | undefined,
                    }))
                  }
                  options={severityOptions}
                  placeholder="All"
                  allowEmpty
                />

                <SelectField
                  id="sla-filter-sla-category"
                  label="SLA status"
                  value={draft.sla_category}
                  onValueChange={(sla_category) =>
                    setDraft((current) => ({
                      ...current,
                      sla_category: sla_category as SlaCategory | undefined,
                    }))
                  }
                  options={slaCategoryOptions}
                  placeholder="All"
                  allowEmpty
                />
              </>
            )}
          />
        }
      />
      <Outlet />
    </>
  )
}
